import { Link } from "react-router-dom";
import type { QueuedJob } from "../api/client";
import { chicagoTime, formatDuration } from "../lib/format";
import { DetailLine, ExpandableRow, PanelTail } from "./PanelTail.js";

/**
 * One queued or parked job, rendered the same way wherever it appears.
 *
 * A per-kind aggregate is the right total but cannot say whose filing is waiting, which
 * separation it would move, or why it has not run, so the queue lists records like every
 * other list on the Operations page, with the per-kind counts beneath as the total.
 *
 * **What the row says, and where.** The summary carries what can be scanned: the kind, the
 * ticker, the subject, the situation, and whether it is about to run, waiting out a
 * backoff, paused, or parked. The opened row carries what has to be read: the company in
 * full, the enqueue time, the claim order, the attempt and reclaim counts, and on a parked
 * row the error that stopped it.
 *
 * `attempts` and `reclaims` are shown apart because they mean opposite things. An attempt
 * is a run that reached a conclusion; a reclaim is a worker that stopped mid-job (a
 * redeploy, ordinarily), and the queue refunds the attempt. Folded into one number, a job
 * being killed and a job going wrong are indistinguishable.
 */
export function QueuedJobRow({
  job,
  open,
  onToggle,
  /** A control for the opened row — the retry button on a parked job, and nothing else. */
  action,
}: {
  job: QueuedJob;
  open: boolean;
  onToggle: () => void;
  action?: React.ReactNode;
}) {
  const enqueued = chicagoTime(job.enqueuedAt);
  const failed = job.failedAt ? chicagoTime(job.failedAt) : null;

  return (
    <ExpandableRow
      open={open}
      onToggle={onToggle}
      summary={
        <>
          {/*
            When it was enqueued, which for a backfill is not when it will run: a queue is
            read for how long something has been sitting there.
          */}
          <span className="fw" title={enqueued?.full ?? job.enqueuedAt}>
            {enqueued?.short ?? "—"}
          </span>
          <span className="ft">{job.kind}</span>
          {job.ticker && <span className="fk">{job.ticker}</span>}
          <span className="fd">{job.subject ?? "no subject"}</span>
          {job.situation && <span className="fsit">{job.situation.name}</span>}
          {job.state === "failed" ? (
            <span className="ff">
              failed after {job.attempts} {job.attempts === 1 ? "try" : "tries"}
            </span>
          ) : (
            /*
              What this row is waiting for, in the slot a finished row uses for its
              outcome.
            */
            <span className="fo">{waitingLabel(job)}</span>
          )}
        </>
      }
      detail={
        <>
          <DetailLine k="enqueued" v={enqueued?.full ?? job.enqueuedAt} />
          {/*
            A pending row is one of three things, and each wants a different response:
            runnable and unclaimed means no worker is taking it, a backoff means the queue
            is deliberately waiting, and paused means somebody stopped the kind.
          */}
          <DetailLine k="status" v={statusSentence(job)} />
          <DetailLine k="company" v={job.company ?? "not a company's filing"} />
          <DetailLine k="ticker" v={job.ticker ?? "none listed"} />
          <DetailLine k="subject" v={job.subject ?? "none"} />
          <DetailLine
            k="situation"
            v={
              job.situation ? (
                <>
                  <Link to={`/situations/${job.situation.id}`}>{job.situation.name}</Link>
                  {/*
                    One filing can sit on several situations, so naming one and staying
                    silent about the others would claim they do not exist.
                  */}
                  {job.situation.alsoOn > 0 && (
                    <span className="dlnote">
                      {" "}
                      · this filing is also on {job.situation.alsoOn} other situation
                      {job.situation.alsoOn === 1 ? "" : "s"}
                    </span>
                  )}
                </>
              ) : (
                "none — this job is not about a situation"
              )
            }
          />
          {/*
            Claim order made checkable: the worker sorts by `priority DESC, run_after`, and
            backfill carries a negative priority.
          */}
          <DetailLine
            k="priority"
            v={
              <>
                {job.priority}
                <span className="dlnote">
                  {" "}
                  · claimed in order of priority, then by when each became runnable
                  {job.priority < 0 ? " — negative is backfill, behind live work" : ""}
                </span>
              </>
            }
          />
          <DetailLine
            k="attempts"
            v={
              job.attempts > 1
                ? `${job.attempts} of ${job.maxAttempts} — this has been tried before`
                : `${job.attempts} of ${job.maxAttempts}`
            }
          />
          {/*
            Shown only when there are any; a zero would be a line on every row saying
            nothing happened.
          */}
          {job.reclaims > 0 && (
            <DetailLine
              k="reclaims"
              v={
                <>
                  {job.reclaims}
                  <span className="dlnote">
                    {" "}
                    · times a worker stopped mid-job and the queue took this back. Not
                    failures — the attempt is refunded each time.
                  </span>
                </>
              }
            />
          )}
          {failed && <DetailLine k="parked" v={failed.full} />}
          {/*
            The error, in full and selectable. A pending row can carry one too: a retry
            waiting out its backoff has already failed once.
          */}
          {job.lastError && (
            <DetailLine
              k={job.state === "failed" ? "error" : "last error"}
              v={<code className="dlerr">{job.lastError}</code>}
            />
          )}
          {action && <DetailLine k="action" v={action} />}
        </>
      }
    />
  );
}

/**
 * What a pending job is waiting for: paused, a retry backoff, or nothing (runnable).
 *
 * Never collapsed into "queued": the first may be a decision the reader forgot, the
 * second is the queue working, and the third is fine or a worker shortage.
 */
export function waitingLabel(job: QueuedJob): string {
  if (job.paused) return "paused";
  if (job.runnableForSeconds < 0) {
    return `next attempt in ${formatDuration(-job.runnableForSeconds)}`;
  }
  return `runnable ${formatDuration(job.runnableForSeconds)}`;
}

/** The same states, plus parked, said in full for the opened row. */
function statusSentence(job: QueuedJob): string {
  if (job.state === "failed") {
    return "Parked. Nothing will claim it until it is retried by hand — the row is kept, never dropped.";
  }
  if (job.paused) {
    return (
      "This kind is paused, so no worker will claim it. Paused work is skipped at claim " +
      "time and never dropped — it runs when the pause lifts."
    );
  }
  if (job.runnableForSeconds < 0) {
    return `Waiting out a retry backoff — claimable in ${formatDuration(-job.runnableForSeconds)}.`;
  }
  return `Runnable, and has been for ${formatDuration(job.runnableForSeconds)}. Whichever worker asks next takes it.`;
}

/** The queue tail, capped by `PanelTail`, with a link to the whole live queue. */
export function QueuedJobTail({ jobs }: { jobs: QueuedJob[] }) {
  return (
    <PanelTail
      items={jobs}
      idOf={(j) => j.id}
      renderRow={(job, open, toggle) => (
        <QueuedJobRow job={job} open={open} onToggle={toggle} />
      )}
      href="/ops/queue"
      linkLabel="The whole queue"
    />
  );
}
